import { GetBankAccountDataResponse } from '@/modules/bank/type'
import { columns, writeAccessorKeys } from './constants'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { useMemo } from 'react'
import { SortableTable } from '@/common/ui/table/sortable-table'
import { SortableRow } from './SortableRow'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { BankQueryKeys } from '@/modules/bank/keys'
import useMutationUpdateBankAccount from '@/modules/bank/api/hooks/useMutationUpdateBankAccount'

interface BankAccountTableProps {
	data?: GetBankAccountDataResponse[]
	isLoading?: boolean
	total?: number
}

export default function BankAccountTable({
	data = [],
	isLoading = false,
	total = 0,
}: BankAccountTableProps) {
	const { data: user } = useQueryProfile()
	const { mutateAsync: updateBankAccount } = useMutationUpdateBankAccount()
	const queryClient = useQueryClient()

	const hasWritePermission = user?.userGroup.writePermission.includes(
		PermissionKeys.MANAGE_BANK_ACCOUNT,
	)
	const filteredColumns = useMemo(
		() =>
			columns.filter(
				(column) =>
					hasWritePermission ||
					('accessorKey' in column &&
						!writeAccessorKeys.includes(column.accessorKey)),
			),
		[hasWritePermission],
	)

	const handleOrderChange = async (
		_newData: GetBankAccountDataResponse[],
		movedItem: GetBankAccountDataResponse,
		newIndex: number,
	) => {
		if (!hasWritePermission) return

		try {
			await updateBankAccount({
				id: movedItem.id,
				payload: {
					order: newIndex + 1,
				},
			})
			queryClient.invalidateQueries({
				queryKey: [BankQueryKeys.BankAccounts],
			})
			toast.success('อัพเดทลำดับสำเร็จ')
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<SortableTable
			data={data}
			columns={filteredColumns}
			searchPlaceholder="ค้นหาบัญชีธนาคาร"
			onOrderChange={handleOrderChange}
			SortableRowComponent={SortableRow}
			totalItems={total}
			isLoading={isLoading}
		/>
	)
}
